import type { Agent } from '@awork/features/agent/models/agent.model'
import type { AgentSkill } from '@awork/features/agent/models/agent-skill.model'
import type { AgentConnector } from '@awork/features/agent/services/agent-service/types'
import type { AgentPrompt } from '@awork/features/agent/models/agent-prompt.model'
import type { User } from '@awork/features/user/models/user.model'
import type { Company } from '@awork/features/company/models/company.model'
import type { Project } from '@awork/features/project/models/project.model'
import type { ProjectTemplate } from '@awork/features/project/models/project-template.model'
import type { Task } from '@awork/features/task/models/task.model'
import type { AutomationTask } from '@awork/features/project/models/automation-task.model'
import type { BadgeColor } from '@awork/_shared/types/color'
import { Color } from '@awork/_shared/types/color'
import type { TaskTemplate } from '@awork/features/task/models/task-template.model'
import type { PublicProjectTemplateProject } from '@awork/features/project/models/public-project-template.model'

export const LAST_USED_LIMIT = 10
export const MOST_USED_IN_PROJECTS_LIMIT = 20

export interface Tag {
  id?: string
  name: string
  color?: BadgeColor
  usageCount?: number
  lastUsedOn?: Date | string
  isNew?: boolean
}

export type AiTagEntity = Agent | AgentSkill | AgentConnector | AgentPrompt
export type AiTagEntityType = 'agent' | 'skill' | 'connector' | 'prompt'

export interface AiTag {
  id: string
  name: string
  color: BadgeColor
  assignmentCount?: number
}

export interface AiTagListItem extends AiTag {
  assignmentCount: number
}

export type AiTagAssignment = { tagId: string } | { name: string; color: string }

export type AiTagChange =
  | { operation: 'saved'; tag: AiTag }
  | { operation: 'merged'; sourceTagId: string; targetTag: AiTag }
  | { operation: 'deleted'; tagId: string }

export interface MergeAiTagResponse {
  sourceTagId: string
  targetTagId: string
  targetTag: AiTag
}

export interface TagsSet {
  label: string
  tags: Tag[]
  limit: number
  sortProp: keyof Tag
}

export const badgeColors: BadgeColor[] = [
  Color.Red,
  Color.Orange,
  Color.Yellow,
  Color.Blue,
  Color.Cyan,
  Color.Green,
  Color.Purple,
  Color.Indigo,
  Color.Steel
]

export type TagSource =
  'user' | 'project' | 'company' | 'task' | 'batchTaskEdit' | 'projectTemplate' | 'taskTemplate' | 'ai'

export type TagOwner =
  | User
  | Company
  | Project
  | Task
  | ProjectTemplate
  | AutomationTask
  | TaskTemplate
  | PublicProjectTemplateProject
  | Agent
  | AgentSkill
  | AgentConnector
  | AgentPrompt

export type TagPopupSource = 'task' | 'project' | 'projectTemplate' | 'company' | 'user' | 'taskTemplate' | 'ai'
export type TagEntity =
  | Project
  | Task
  | ProjectTemplate
  | AutomationTask
  | Company
  | User
  | TaskTemplate
  | PublicProjectTemplateProject
  | Agent
  | AgentSkill
  | AgentConnector
  | AgentPrompt

export enum TagFilterEntity {
  Task = 'task',
  Project = 'project',
  User = 'user'
}

/**
 * Selects a stable badge color from the tag name.
 * @param {string} name
 * @returns {BadgeColor}
 */
export function getRandomBadgeColor(name: string): BadgeColor {
  if (!name) {
    return badgeColors[0]
  }

  return badgeColors[(name.length + name.charCodeAt(name.length - 1)) % badgeColors.length]
}
